import * as React from 'react';
import { Input as TextInput, MarkdownEditor } from '../../../components/ui';
import InputMask from 'react-input-mask';
import {SortableContainer, SortableElement} from 'react-sortable-hoc';
import {
  InputEvent,
  WorkoutProps,
  ExerciseWorkoutSelectProps,
  WorkoutGroupProps,
} from '../../types';
import {
  WorkoutContainer,
  WorkoutContainerHead,
  WorkoutContainerHeader,
  WorkoutContainerEmpty,
  WorkoutContainerBody,
  WorkoutExerciseTitle,
} from './WorkoutComponent.style';
import {WorkoutToggleContainer} from './WorkoutGroups.style';

import {WorkoutGroupContainerMinified} from './WorkoutGroups.style';

import {
  ButtonDeleteComponent,
  ButtonToggleContentClean,
  ButtonState,
} from '../button';
import {ButtonRowHeaderContainer} from '../button/Button.style';

import {
  ExerciseContainer,
  ExerciseContainerHeader,
  ExerciseContainerBody,
  ExerciseColumn,
} from '../exercises/ExerciseComponents.style';

import {ExerciseEmptyComponent, ExerciseComponent} from '../exercises';

type WorkoutEmptyComponentProps = {
  workoutGroup: WorkoutGroupProps;
  workoutGroupIndex: number;
  createWorkout?: (workoutGroup: WorkoutGroupProps) => void;
};

type UpdateWorkoutProps = {(workout, index): void};

/**
 * WorkoutComponent Props
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for updateWorkout, deleteWorkout, etc.
 * - Now accepts these as props directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
type WorkoutComponentProps = {
  index: number;
  workoutGroupIndex: number;
  workoutGroup: WorkoutGroupProps;
  workout: WorkoutProps;
  errors?: any;
  exerciseListDefault: ExerciseWorkoutSelectProps[];
  updateWorkout?: (workout: any, index: number, workoutGroup: any) => void;
  deleteWorkout?: (workout: any, workoutGroup: any) => void;
  createWorkoutExerciseBySelected?: (
    selected: any,
    workout: any,
    workoutGroup: any,
    exerciseData?: any,
  ) => void;
  updateWorkoutpPosition?: (
    direction: string,
    workoutGroup: any,
    currentIndex: number,
  ) => void;
};

/**
 * WorkoutComponent
 * Legacy component for displaying and editing a workout
 *
 * Note: This component is kept for backward compatibility but is no longer
 * used by the main WorkoutRoutineForm flow, which uses WorkoutField instead.
 */
export const WorkoutComponent = ({
  index,
  workout,
  workoutGroup,
  workoutGroupIndex,
  errors = {},
  exerciseListDefault,
  updateWorkout,
  deleteWorkout,
  createWorkoutExerciseBySelected,
  updateWorkoutpPosition,
}: WorkoutComponentProps): JSX.Element => {
  const onChangeDescription = (value) => {
    if (updateWorkout) {
      updateWorkout({...workout, description: value}, index, workoutGroup);
    }
  };

  const onChangeInput = (event) => {
    const newWorkoutValue = {
      ...workout,
      [event.target.name]: event.target.value,
    };
    if (updateWorkout) {
      updateWorkout(newWorkoutValue, index, workoutGroup);
    }
  };

  const renderExercisesWorkout = () => {
    const {exerciseWorkouts = []} = workout;
    return exerciseWorkouts
      .filter((exercise) => !exercise['_destroy'])
      .map((exercise, exerciseIndex) => (
        <ExerciseComponent
          key={`exercise-${exercise?.id || exercise.tempId}-${exerciseIndex}`}
          exerciseIndex={exerciseIndex}
          workoutGroupIndex={workoutGroupIndex}
          workoutIndex={index}
          workoutExercise={exercise}
          workout={workout}
          workoutGroup={workoutGroup}
          defaultOptions={[...(exerciseListDefault || []), exercise.exercise]}
        />
      ));
  };

  const toggleContentWorkoutGroup = (value) => {
    setToggleContent(value);
  };

  const [toggleContent, setToggleContent] = React.useState(ButtonState.open);

  const switchToggleContentWorkoutGroup = () => {
    const newState =
      toggleContent === ButtonState.open
        ? ButtonState.closed
        : ButtonState.open;
    setToggleContent(newState);
  };

  const handleMoveUp = () => {
    if (updateWorkoutpPosition) {
      updateWorkoutpPosition('up', workoutGroup, index);
    }
  };

  const handleMoveDown = () => {
    if (updateWorkoutpPosition) {
      updateWorkoutpPosition('down', workoutGroup, index);
    }
  };

  const handleDelete = () => {
    if (deleteWorkout) {
      deleteWorkout(workout, workoutGroup);
    }
  };

  const handleCreateExercise = (selected?: any, exerciseData?: any) => {
    if (createWorkoutExerciseBySelected) {
      createWorkoutExerciseBySelected(
        selected,
        workout,
        workoutGroup,
        exerciseData,
      );
    }
  };

  return (
    <WorkoutContainer>
      <WorkoutContainerHead>
        <WorkoutToggleContainer>
          <i
            onClick={handleMoveUp}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleMoveUp()}
            className="far fa-long-arrow-up"
            role="button"
            tabIndex={0}
          />
          <i
            onClick={handleMoveDown}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleMoveDown()}
            className="far fa-long-arrow-down"
            role="button"
            tabIndex={0}
          />
        </WorkoutToggleContainer>
        <TextInput
          s={8}
          id={`workoutGroup-${workoutGroupIndex}-workout-${index}`}
          name={`name`}
          onChange={onChangeInput}
          value={workout.name}
          placeholder={`Dê um nome para o bloco do treino (Ex: Aquecimento, força etc)`}
          label={null}
          inputClassName={errors?.name ? 'invalid' : ' '}
          error={errors?.name}
        />
        <div className="col s4 input-field">
          <InputMask
            id={`workoutGroup-${workoutGroupIndex}-workout-${index}-time_text`}
            name={`time_text`}
            type="tel"
            mask={'99:99'}
            maskChar="0"
            onChange={onChangeInput}
            value={workout.time_text || ''}
            placeholder={'Defina um tempo'}
            className={errors?.timeText ? 'invalid' : ' '}
          />
          <span className="helper-text" data-error={errors?.timeText}></span>
        </div>
        <ButtonRowHeaderContainer top={'0'} right={'0'}>
          <ButtonToggleContentClean
            onPress={toggleContentWorkoutGroup}
            buttonState={toggleContent}
            title="Minimize o conteúdo para editar mais facilmente"
          />
          <ButtonDeleteComponent
            onPress={handleDelete}
            name="Etapa do treino"
            confirmMessage={
              'Você tem certeza que deseja apagar essa etapa de treino?'
            }
            title="Apagar o treino, suas etapas e exercício"
          />
        </ButtonRowHeaderContainer>
      </WorkoutContainerHead>
      {toggleContent === ButtonState.open && (
        <WorkoutContainerBody>
          <MarkdownEditor
            s={12}
            id={`workoutGroup-${workoutGroupIndex}-workout-${index}-description`}
            onChange={(value) => onChangeDescription(value)}
            value={workout.description}
            label={'Descreva o bloco de treino ou observações'}
            placeholder={`Escreva aqui suas observacões`}
            error={errors?.description}
          />
          <div className="col s12">
            <WorkoutExerciseTitle>Exercícios</WorkoutExerciseTitle>
          </div>
          <div className="col s12">
            <ExerciseContainer>
              <ExerciseContainerBody>
                {renderExercisesWorkout()}
                <ExerciseEmptyComponent
                  workoutGroupIndex={workoutGroupIndex}
                  workoutIndex={index}
                  createExerciseComponent={handleCreateExercise}
                  defaultOptions={exerciseListDefault || []}
                />
              </ExerciseContainerBody>
            </ExerciseContainer>
          </div>
        </WorkoutContainerBody>
      )}
      {toggleContent === ButtonState.closed && (
        <WorkoutGroupContainerMinified
          onClick={switchToggleContentWorkoutGroup}>
          <i className="far fa-ellipsis-h"></i>
        </WorkoutGroupContainerMinified>
      )}
    </WorkoutContainer>
  );
};

/**
 * WorkoutEmptyComponent
 * Legacy component for adding a new workout
 */
export const WorkoutEmptyComponent = ({
  workoutGroup,
  workoutGroupIndex,
  createWorkout,
}: WorkoutEmptyComponentProps): JSX.Element => {
  const handleCreateWorkout = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (createWorkout) {
      createWorkout(workoutGroup);
    }
  };

  return (
    <WorkoutContainer className="clean-top add-new">
      <WorkoutContainerEmpty
        as="button"
        type="button"
        onClick={handleCreateWorkout}
        style={{
          border: 'none',
          background: 'none',
          width: '100%',
          cursor: 'pointer',
        }}>
        Adicionar novo bloco de treino
      </WorkoutContainerEmpty>
    </WorkoutContainer>
  );
};
